import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export function resolveBackendUrl(configuredUrl, locationLike) {
  const location = locationLike || {};
  const origin = (location.origin || '').replace(/\/$/, '');
  const hostname = (location.hostname || '').toLowerCase();
  const configured = (configuredUrl || '').trim().replace(/\/$/, '');

  // PROVI production is served behind the same-origin Nginx /api proxy.
  if (hostname === 'proviapp.com' || hostname === 'www.proviapp.com') {
    return origin;
  }

  // Never let an HTTP API target create mixed content from an HTTPS page.
  if (location.protocol === 'https:' && /^http:\/\//i.test(configured)) {
    return origin;
  }

  if (configured) return configured;

  if (location.port === '3000') {
    return `${location.protocol}//${location.hostname}:8000`;
  }

  return origin;
}

const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = resolveBackendUrl(
  configuredBackendUrl,
  typeof window !== 'undefined' ? window.location : undefined
);
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export function isConnectionError(error) {
  return axios.isAxiosError(error)
    && !error.response
    && (error.code === 'ERR_NETWORK' || error.message === 'Network Error');
}

function logUnexpectedLoginError(error) {
  console.error('Unexpected login error:', {
    name: error?.name || 'Error',
    code: error?.code || null,
    message: error?.message || 'Unknown error'
  });
}

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Algo salio mal. Por favor intenta de nuevo.';
  if (typeof detail === 'string') {
    if (detail === 'Email already registered' || detail.includes('registrado para este tipo de cuenta')) {
      return 'Este correo ya esta registrado para este tipo de cuenta.';
    }
    if (detail === 'Invalid credentials') return 'Correo o contraseña incorrectos.';
    return detail;
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean);
    return messages.length > 0 ? messages.join(' ') : 'Revisa los datos ingresados.';
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (error) {
      if (error.response?.status !== 401) {
        console.error('Auth check error:', error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, expectedRole) => {
    try {
      const payload = { email: email.trim().toLowerCase(), password };
      if (expectedRole) {
        payload.expected_role = expectedRole;
      }
      const { data } = await axios.post(`${API}/auth/login`, payload, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (e) {
      if (isConnectionError(e)) {
        return { success: false, error: 'No se pudo conectar con el servidor. Verifica tu conexion o intenta mas tarde.' };
      }
      if (e.response?.status === 401) {
        return { success: false, error: 'Correo o contraseña incorrectos.' };
      }
      if (e.response?.status === 403 || e.response?.status === 422) {
        return { success: false, error: formatApiErrorDetail(e.response?.data?.detail) };
      }
      if (e.response?.status === 429) {
        return { success: false, error: 'Demasiados intentos. Espera un momento e intenta de nuevo.' };
      }
      if (e.response?.status >= 500) {
        return { success: false, error: 'Ocurrio un error interno. Intenta de nuevo mas tarde.' };
      }
      if (!axios.isAxiosError(e)) {
        logUnexpectedLoginError(e);
        return { success: false, error: 'No se pudo completar el inicio de sesion. Intenta de nuevo.' };
      }
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error: errorMsg };
    }
  };

  const register = async (formData) => {
    try {
      const payload = {
        ...formData,
        email: formData.email?.trim().toLowerCase()
      };
      const { data } = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      setUser(data);
      return { success: true, data };
    } catch (e) {
      if (isConnectionError(e)) {
        return { success: false, error: 'No se pudo conectar con el servidor. Verifica tu conexion o intenta mas tarde.' };
      }
      const errorMsg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    isAuthenticated: Boolean(user)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

export { API, BACKEND_URL };
